import { createContext, useCallback, useMemo, useState } from "react";
import {
	DefaultMessageDuration,
	MessageContextType,
	Namespace,
	MessageProviderProps,
	Message,
	IdentifiableMessage,
} from "./types";

const defaultState: MessageContextType = {
	messages: new Map<string, IdentifiableMessage[]>(),
	show: () => {},
	hide: () => {},
};

export const MessageContext = createContext<MessageContextType>(defaultState);

const MessageProvider = ({ children }: MessageProviderProps) => {
	const [messages, setMessages] = useState<Map<string, IdentifiableMessage[]>>(
		new Map<string, IdentifiableMessage[]>()
	);

	const show = useCallback(
		(namespace: Namespace, message: Message) => {
			const duration = message.duration ?? DefaultMessageDuration;

			if (duration <= 0) {
				return;
			}

			const id = message.key ?? crypto.randomUUID();
			const newMessage = { ...message, id };
			setMessages((prev) => {
				const currentMessages = prev.get(namespace) ?? [];
				if (currentMessages.find((m) => m.text == message.text)) {
					return prev;
				}

				const updatedMap = new Map(
					prev.set(namespace, [...currentMessages, newMessage])
				);

				if (!message.isPermanent) {
					setTimeout(() => {
						hide(namespace, id);
					}, duration);
				}

				return updatedMap;
			});
		},
		[setMessages]
	);

	const hide = useCallback(
		(namespace: Namespace, id: string) => {
			setMessages((prev) => {
				const currentMessages = prev.get(namespace) ?? [];

				if (
					!!currentMessages.length &&
					currentMessages.some((m) => m.id === id)
				) {
					const updatedMap = new Map(
						prev.set(
							namespace,
							currentMessages.filter((message) => message.id !== id)
						)
					);

					return updatedMap;
				}

				return prev;
			});
		},
		[setMessages]
	);

	const contextValue = useMemo(
		() => ({ messages: messages, show, hide }),
		[messages, show, hide]
	);

	return (
		<MessageContext.Provider value={contextValue}>
			{children}
		</MessageContext.Provider>
	);
};

export default MessageProvider;
